import { useRef } from 'react'
import { useUnmount } from 'react-use'
import type { Promisify } from '../type'
import { UnFile } from '../unFile'

const createObjectURL = (blob: UnFile | Blob) =>
  blob instanceof UnFile ? blob.createUrl() : URL.createObjectURL(blob)
const revokeObjectURL = (blob: UnFile | Blob | null | undefined, url: string) =>
  blob instanceof UnFile ? blob.revokeUrl() : URL.revokeObjectURL(url)

export function useObjectUrl<T extends UnFile | Blob | null | undefined>(
  blob: T,
): T extends UnFile | Blob ? string : undefined {
  const url = useRef<string>(null)
  const blobRef = useRef<T>(null)

  if (blob !== blobRef.current) {
    if (url.current) revokeObjectURL(blobRef.current, url.current)
    if (blob) url.current = createObjectURL(blob)
    else url.current = null
    blobRef.current = blob ?? null
  }

  useUnmount(() => {
    if (url.current) revokeObjectURL(blobRef.current, url.current)
  })

  return (url.current ?? undefined) as any
}

export function scopedObjectUrl<T>(
  blob: UnFile | Blob,
  fn: (url: string) => T,
): T {
  const url = createObjectURL(blob)
  try {
    return fn(url)
  } finally {
    if (url) revokeObjectURL(blob, url)
  }
}

export async function asyncScopedObjectUrl<T>(
  blob: UnFile | Blob,
  fn: (url: string) => T,
): Promisify<T> {
  const url = createObjectURL(blob)
  try {
    return await fn(url)
  } finally {
    if (url) revokeObjectURL(blob, url)
  }
}
